import {
  Box, Divider, Drawer, List, ListItem, ListItemIcon,
  ListItemText, Toolbar, Typography, useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../components/AuthContext";

import DashboardIcon   from "@mui/icons-material/Dashboard";
import PeopleIcon      from "@mui/icons-material/People";
import SchoolIcon      from "@mui/icons-material/School";
import AssignmentIcon  from "@mui/icons-material/Assignment";
import LayersIcon      from "@mui/icons-material/Layers";
import WorkIcon        from "@mui/icons-material/Work";
import SecurityIcon    from "@mui/icons-material/Security";
import GroupIcon       from "@mui/icons-material/Group";
import BusinessIcon    from "@mui/icons-material/Business";
import ApartmentIcon   from "@mui/icons-material/Apartment";
import FactCheckIcon   from "@mui/icons-material/FactCheck";
import AssessmentIcon  from "@mui/icons-material/Assessment";
import EventNoteIcon   from "@mui/icons-material/EventNote";
import DescriptionIcon from "@mui/icons-material/Description";

export const DRAWER_WIDTH = 240;

function NavItem({ to, icon, text, permission, onClick }) {
  const location = useLocation();
  const { hasPermission } = useAuth();

  if (permission && !hasPermission(permission)) return null;

  const active = location.pathname === to;

  return (
    <ListItem
      button
      component={Link}
      to={to}
      onClick={onClick}
      sx={{
        borderRadius: "8px",
        mx: 1,
        mb: 0.5,
        width: "auto",
        color: active ? "white" : "rgba(255,255,255,0.75)",
        background: active
          ? "rgba(255,255,255,0.18)"
          : "transparent",
        "&:hover": {
          background: active
            ? "rgba(255,255,255,0.22)"
            : "rgba(255,255,255,0.1)",
          color: "white",
        },
        transition: "background 0.15s, color 0.15s",
      }}
    >
      <ListItemIcon sx={{ color: "inherit", minWidth: 36 }}>
        {icon}
      </ListItemIcon>
      <ListItemText
        primary={text}
        primaryTypographyProps={{
          fontSize: 14,
          fontWeight: active ? 600 : 400,
          color: "inherit",
        }}
      />
    </ListItem>
  );
}

function SidebarContent({ onNavigate }) {
  return (
    <Box
      sx={{
        width: DRAWER_WIDTH,
        height: "100%",
        background: "linear-gradient(180deg, #8B0000 0%, #C62828 100%)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Brand header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 2.5,
        }}
      >
        <Box
          sx={{
            bgcolor: "rgba(255,255,255,0.2)",
            borderRadius: "10px",
            p: 0.8,
            display: "flex",
          }}
        >
          <SchoolIcon sx={{ color: "white", fontSize: 22 }} />
        </Box>
        <Box>
          <Typography
            variant="body2"
            sx={{ color: "white", fontWeight: 700, lineHeight: 1.2, fontSize: 13 }}
          >
            Training
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "rgba(255,255,255,0.65)", fontSize: 11 }}
          >
            Training System
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.15)", mx: 2, mb: 1 }} />

      <List sx={{ flex: 1, pt: 0.5, px: 0, overflowY: "auto", overflowX: "hidden" }}>
        <NavItem to="/dashboard"     icon={<DashboardIcon  fontSize="small" />} text="Dashboard"       onClick={onNavigate} />
        <NavItem to="/empleados"     icon={<PeopleIcon     fontSize="small" />} text="Employees"       onClick={onNavigate} permission="EMPLEADOS_VER" />

        <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", fontWeight: 600, fontSize: 10, letterSpacing: 1, textTransform: "uppercase" }}>
            Catalogs
          </Typography>
        </Box>
        <NavItem to="/puestos"       icon={<WorkIcon       fontSize="small" />} text="Positions"       onClick={onNavigate} permission="PUESTOS_VER" />
        <NavItem to="/areas"         icon={<BusinessIcon   fontSize="small" />} text="Areas"           onClick={onNavigate} permission="AREAS_VER" />
        <NavItem to="/departamentos" icon={<ApartmentIcon  fontSize="small" />} text="Departments"     onClick={onNavigate} permission="DEPARTAMENTOS_VER" />
        <NavItem to="/fichas"        icon={<DescriptionIcon fontSize="small" />} text="Working Sheets"  onClick={onNavigate} permission="FICHAS_VER" />

        <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", fontWeight: 600, fontSize: 10, letterSpacing: 1, textTransform: "uppercase" }}>
            Training
          </Typography>
        </Box>
        <NavItem to="/cursos"        icon={<SchoolIcon     fontSize="small" />} text="Courses"         onClick={onNavigate} permission="CURSOS_VER" />
        <NavItem to="/versiones"     icon={<LayersIcon     fontSize="small" />} text="Versions"        onClick={onNavigate} permission="VERSIONES_VER" />
        <NavItem to="/asignaciones"  icon={<AssignmentIcon fontSize="small" />} text="Assignments"     onClick={onNavigate} permission="ASIGNACIONES_VER" />
        <NavItem to="/capacitaciones" icon={<FactCheckIcon  fontSize="small" />} text="Record Training" onClick={onNavigate} permission="CAPACITACIONES_VER" />
        <NavItem to="/reportes/horas" icon={<AssessmentIcon fontSize="small" />} text="Man-hours"      onClick={onNavigate} permission="CAPACITACIONES_VER" />
        <NavItem to="/planes"         icon={<EventNoteIcon  fontSize="small" />} text="Training Plans" onClick={onNavigate} permission="PLANES_VER" />

        <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", fontWeight: 600, fontSize: 10, letterSpacing: 1, textTransform: "uppercase" }}>
            Administration
          </Typography>
        </Box>
        <NavItem to="/admin/usuarios" icon={<GroupIcon    fontSize="small" />} text="Users"           onClick={onNavigate} permission="USUARIOS_VER" />
        <NavItem to="/admin/roles"    icon={<SecurityIcon fontSize="small" />} text="Roles"            onClick={onNavigate} permission="ROLES_VER" />
      </List>
    </Box>
  );
}

export default function Sidebar({ open, onClose }) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const drawerSx = {
    width: DRAWER_WIDTH,
    flexShrink: 0,
    "& .MuiDrawer-paper": {
      width: DRAWER_WIDTH,
      boxSizing: "border-box",
      border: "none",
    },
  };

  if (isDesktop) {
    return (
      <Drawer variant="permanent" sx={drawerSx}>
        <SidebarContent />
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={drawerSx}
    >
      <SidebarContent onNavigate={onClose} />
    </Drawer>
  );
}
